import { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "react-bootstrap-icons";
import { Modal } from "react-bootstrap";
// import http from "services/http";
import http from "services/httpClient";

interface DeviceLabel {
  column_index: number;
  label: string;
  is_enabled: boolean;
  multiplier?: number;
  offset?: number;
  unit?: string;
}

interface DeviceDataRow {
  timestamp: string;
  values: Record<string, number | null>;
}

interface DeviceExportTabProps {
  deviceId: number;
  deviceName: string;
}

export function DeviceExportTab({ deviceId, deviceName }: DeviceExportTabProps) {
  const [labels, setLabels] = useState<DeviceLabel[]>([]);
  const [selectedCols, setSelectedCols] = useState<number[]>([]);
  const [exportStart, setExportStart] = useState<Date | null>(null);
  const [exportEnd, setExportEnd] = useState<Date | null>(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [tempEnd, setTempEnd] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  

  const fetchLabels = async () => {
    const [rawRes, calcRes] = await Promise.all([
      http.get(`/device/${deviceId}/labels`),
      http.get(`/calculated_params/${deviceId}`),
    ]);

    const rawLabels: DeviceLabel[] = rawRes.data;
    
    const calcLabels: DeviceLabel[] = calcRes.data.map((item: any) => ({
      column_index: item.column_index,
      label: item.result_label,
      is_enabled: true,
      multiplier: 1,
      offset: 0,
      unit: item.unit || "",
    }));

    setLabels([...rawLabels, ...calcLabels]);
    setSelectedCols([]);
  };


  useEffect(() => {
    fetchLabels();
  }, []);


  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  };

  const labelMetaByIndex = new Map<number, DeviceLabel>(
    labels.map((l) => [l.column_index, l])
  );

   const formatDateForQuery = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;

  return (
    <>
      <Row className="mb-3">
        <Col>
          <strong>Select Columns:</strong>
          <div className="d-flex flex-wrap">
            {labels
              .filter((label) => label.is_enabled) // Only show enabled labels
              .map((label) => (
                <Form.Check
                  key={label.column_index}
                  inline
                  label={label.label}
                  checked={selectedCols.includes(label.column_index)}
                  onChange={() =>
                    setSelectedCols((prev) =>
                      prev.includes(label.column_index)
                        ? prev.filter((i) => i !== label.column_index)
                        : [...prev, label.column_index]
                    )
                  }
                />
            ))}
          </div>
        </Col>
      </Row>
      <Row className="mb-2 justify-content-between">
      <Col md="auto" className="d-flex align-items-end">
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center gap-2"
          onClick={() => {
            setTempStart(exportStart);
            setTempEnd(exportEnd);
            setShowCalendarModal(true);
          }}
        >
          <Calendar />
          {exportStart && exportEnd
            ? `${formatDateForQuery(exportStart)} → ${formatDateForQuery(exportEnd)}`
            : "Select Date Range"}
        </Button>
      </Col>
      <Col className="d-flex justify-content-end align-items-end">
        <Button
          onClick={async () => {
            if (!exportStart || !exportEnd || selectedCols.length === 0) return;
            setIsExporting(true);
            const params = new URLSearchParams();
            selectedCols.forEach((col) => params.append("columns", col.toString()));

            params.append("start", formatDateForQuery(exportStart));
            params.append("end", formatDateForQuery(exportEnd));

            try {
              const res = await http.get(`/data/${deviceId}/chart_data?${params}`);
              const data: DeviceDataRow[] = res.data;
              if (data.length > 0) {
                const now = new Date().toLocaleString().replace(/[:.]/g, "-");
                const filename = `${deviceName}_${now}.csv`;
                const colLabels = selectedCols.map((index) => {
                  const meta = labelMetaByIndex.get(index);
                  const name = meta?.label ?? `col${index}`;
                  const unit = meta?.unit ? ` (${meta.unit})` : "";
                  return `${name}${unit}`;
                });
                const header = ["timestamp", ...colLabels];
                const rows = data.map((row) => [
                  formatTimestamp(row.timestamp),
                  ...selectedCols.map((colIndex) => {
                    const raw = row.values[colIndex.toString()];
                    if (raw === null || raw === undefined) return "";

                    const meta = labelMetaByIndex.get(colIndex);
                    const m = meta?.multiplier ?? 1;
                    const o = meta?.offset ?? 0;

                    const v = raw * m + o;

                    // optional: fixed 3 decimals as number/string
                    return (Math.round(v * 1000) / 1000).toString();
                  }),
                ]);
                const csvContent = [
                  `Device Name:,${deviceName}`,
                  "",
                  header.join(","),
                  ...rows.map((r) => r.join(",")),
                ].join("\n");
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            } catch (err) {
              // console.error("Export error:", err);
            } finally {
              setIsExporting(false);
            }
          }}
          size="sm"
          variant="success"
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Exporting...
            </>
          ) : (
            "Export CSV"
          )}
        </Button>

      </Col>
    </Row>


<Modal
  show={showCalendarModal}
  onHide={() => setShowCalendarModal(false)}
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Select Export Date Range</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form.Group className="mb-3">
      <Form.Label>Start Date</Form.Label>
      <DatePicker
        selected={tempStart}
        onChange={(date) => setTempStart(date)}
        showTimeSelect
        timeFormat="HH:mm:ss"
        timeIntervals={1}
        dateFormat="yyyy-MM-dd HH:mm:ss"
        timeCaption="Time"
        className="form-control"
        placeholderText="Start Date & Time"
      />
    </Form.Group>
    <Form.Group>
      <Form.Label>End Date</Form.Label>
      <DatePicker
        selected={tempEnd}
        onChange={(date) => {
          if (date) {
            const endWithTime = new Date(date);
            endWithTime.setHours(23, 59, 59, 999);
            setTempEnd(endWithTime);
          } else {
            setTempEnd(null);
          }
        }}
        showTimeSelect
        timeFormat="HH:mm:ss"
        timeIntervals={1}
        dateFormat="yyyy-MM-dd HH:mm:ss"
        timeCaption="Time"
        className="form-control"
        placeholderText="End Date & Time"
      />

    </Form.Group>
  </Modal.Body>
  <Modal.Footer>
    <Button
      variant="secondary"
      onClick={() => setShowCalendarModal(false)}
    >
      Cancel
    </Button>
    <Button
      variant="primary"
      onClick={() => {
        if (tempStart && tempEnd) {
          setExportStart(tempStart);
          setExportEnd(tempEnd);
        }
        setShowCalendarModal(false);
      }}
    >
      Apply
    </Button>
  </Modal.Footer>
</Modal>

    </>
  );
}

export default DeviceExportTab;